import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type {
  ApDocumentType,
  ApInvoiceFee,
  ApInvoiceItem,
} from "@/accounts-payable/types";
import {
  formatDollars,
  hasPricebookMatch,
  matchedPricebookItemLabel,
} from "@/accounts-payable/utils";
import { formatCentsOrDash } from "@/core/utils/money";
import { PricebookMatchButton } from "./pricebook-match";
import { SectionLabel } from "./shared";

/**
 * A document's extracted lines, each with the pricebook item it is matched to
 * and the way to change that match. Same list idiom as the action cards' line.
 * An invoice's fees, printed in its totals rather than as lines, follow as
 * rows of their own.
 */
export const LineItemsSection = ({
  items,
  businessId,
  documentId,
  documentType,
  agentRunId,
  fees = [],
}: {
  items: ApInvoiceItem[];
  businessId: string | null;
  documentId: string;
  documentType: ApDocumentType;
  /** The document's newest agent run id. */
  agentRunId: string | null;
  /** Fees printed between the subtotal and the total ("Surcharge"). */
  fees?: ApInvoiceFee[];
}) => {
  if (items.length === 0 && fees.length === 0) return null;

  return (
    <Stack className="w-full" align="start" gap={2}>
      <SectionLabel>
        {items.length > 0 ? `Line items (${items.length})` : "Fees"}
      </SectionLabel>
      <Stack align="start" gap={4} className="w-full">
        {items.map((item) => (
          <Stack align="start" key={item.id} gap={1} className="w-full">
            <Stack
              className="w-full"
              direction="row"
              justify="between"
              gap={4}
              align="start"
            >
              <Stack align="start" gap={0} className="min-w-0">
                <Text>
                  {item.quantity} × {item.description}
                </Text>
                {item.sku && (
                  <Text variant="caption" tone="muted" className="font-mono">
                    {item.sku}
                  </Text>
                )}
                {item.serial_numbers && item.serial_numbers.length > 0 && (
                  <Text variant="caption" tone="muted">
                    S/N {item.serial_numbers.join(", ")}
                  </Text>
                )}
              </Stack>
              <Text className="flex-none tabular-nums">
                {formatDollars(item.unit_cost)}
              </Text>
            </Stack>
            <Stack
              className="w-full"
              direction="row"
              justify="between"
              gap={4}
              align="center"
            >
              <PricebookMatchSummary item={item} />
              <PricebookMatchButton
                businessId={businessId}
                documentId={documentId}
                documentType={documentType}
                agentRunId={agentRunId}
                item={item}
              />
            </Stack>
          </Stack>
        ))}
        {fees.map((fee, index) => (
          <Stack
            // Fees have no id; their order is the invoice's.
            key={index}
            className="w-full"
            direction="row"
            justify="between"
            gap={4}
            align="start"
          >
            <Stack align="start" gap={0} className="min-w-0">
              <Text>{fee.description}</Text>
              <Text variant="caption" tone="muted">
                Fee
              </Text>
            </Stack>
            <Text className="flex-none tabular-nums">
              {formatCentsOrDash(fee.amount_cents)}
            </Text>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

/** "Pricebook: <item> · $cost (manual)", or why there is no item. */
const PricebookMatchSummary = ({ item }: { item: ApInvoiceItem }) => {
  const manual = item.pricebook_match_strategy === "manual";
  if (!hasPricebookMatch(item)) {
    return (
      <Text variant="caption" tone="muted">
        {manual ? "Not in the pricebook (set by hand)" : "No pricebook match"}
      </Text>
    );
  }

  return (
    <Text variant="caption" tone="muted" className="min-w-0">
      Pricebook: {matchedPricebookItemLabel(item)}
      {item.pricebook_cost != null &&
        ` · ${formatDollars(item.pricebook_cost)}`}
      {manual && <span className="text-subtle"> (set by hand)</span>}
    </Text>
  );
};
